import React, { useLayoutEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import styled from 'styled-components'

import { useAnnotationHighlightStore } from 'features/ask-yogi/annotation-highlight-store'
import {
  buildAnnotationPath,
  isFeatureSupported,
} from 'features/ask-yogi/utils/annotation-routing'
import { routeNameMap } from 'features/project/partials/project-layout/route-name-map'
import { useProjectStore } from 'features/project/store'
import type { HashNavigationState } from 'routes/navigation-state'

export const NavAnnotationPreview: React.FC = () => {
  const navigate = useNavigate()
  const highlight = useAnnotationHighlightStore((s) => s.highlight)
  const cancelHide = useAnnotationHighlightStore((s) => s.cancelHide)
  const scheduleHide = useAnnotationHighlightStore((s) => s.scheduleHide)
  const clearHighlight = useAnnotationHighlightStore((s) => s.clearHighlight)
  const { projectId } = useProjectStore((s) => ({ projectId: s.projectId }))
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [offsetTop, setOffsetTop] = useState(0)
  const feature = highlight?.feature

  useLayoutEffect(() => {
    if (!feature) return

    const computeOffset = (): void => {
      const navItem = document.querySelector<HTMLElement>(
        `[data-nav-url="${feature}"]`,
      )
      const container = containerRef.current?.parentElement
      if (!navItem || !container) return

      const navItemRect = navItem.getBoundingClientRect()
      const containerRect = container.getBoundingClientRect()
      setOffsetTop(navItemRect.top - containerRect.top)
    }

    computeOffset()
    window.addEventListener('resize', computeOffset)
    return () => window.removeEventListener('resize', computeOffset)
  }, [feature])

  if (!highlight) return null

  const { annotation } = highlight
  const featureName = routeNameMap[annotation.feature] ?? annotation.feature
  const displayLabel =
    annotation.label ?? `${annotation.action} ${annotation.resource}`
  const supported = isFeatureSupported(annotation.feature)
  const path =
    supported && projectId ? buildAnnotationPath(projectId, annotation) : null

  return (
    <PreviewContainer ref={containerRef} $offsetTop={offsetTop}>
      <FeatureName>{featureName}</FeatureName>
      <ResourceLabel>{displayLabel}</ResourceLabel>
      {path && (
        <OpenCta
          onMouseEnter={cancelHide}
          onMouseLeave={scheduleHide}
          // As the confirmation card does: a saved view opens exactly as
          // saved, not under the user's current filters. Dashboards ignore it.
          onClick={() => {
            clearHighlight()
            navigate(path, {
              state: { hashAuthority: 'url' } satisfies HashNavigationState,
            })
          }}
        >
          Open in {featureName} →
        </OpenCta>
      )}
    </PreviewContainer>
  )
}

const PreviewContainer = styled.div<{ $offsetTop: number }>`
  position: absolute;
  left: calc(100% + 8px);
  top: 0;
  transform: translateY(${({ $offsetTop }) => $offsetTop}px);
  width: 240px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 12px;
  z-index: 100;
  pointer-events: none;
`

const FeatureName = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #888;
  margin-bottom: 4px;
`

const ResourceLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #1a1a1a;
  margin-bottom: 10px;
  word-break: break-word;
`

const OpenCta = styled.button`
  all: unset;
  pointer-events: auto;
  cursor: pointer;
  font-size: 13px;
  color: #2563eb;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`
